/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { modulesNameSynonymMapper } from "../../services/constants";
import { defaultModuleNamesMapper, defaultModulesMapper, formCategories, LAYOUTS_SIDEBAR_CELL_ID } from "@Helpers";
import { getBackendUrl } from "@Utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import axiosWrapper from "@AxiosWrapper";
import { useTranslation } from "@i18n";
import { getPortalCustomModules } from "../../actions/portalSetting.helper";
import { setProjectSideBarIconsAndNameMapper } from "../../reducers/projectSetting";
import { RootState } from "@Reducers/index";
import type { CustomModuleItem } from "@ServerTypes/portal/portal.schema";

/**
 * `defaultModulesMapper` is declared `: any` (packages/helpers/constants.ts), so every
 * `defaultModulesMapper.X` read arrives untyped. It is narrowed at this read rather than at the
 * declaration, which lives in a package this file does not own — the same way `CreateIssueModal` and
 * `AddContractModal` do it. Each fallback below repeats that mapper's own entry (note `CLASH` maps to
 * `"CLASH_TEST"`, the one key whose value differs from its name), so every sidebar entry resolves to
 * exactly the `defaultModuleNamesMapper` id it resolved to before.
 */
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;
const modulesMapper: Record<string, unknown> = isRecord(defaultModulesMapper) ? defaultModulesMapper : {};
const moduleFlag = (key: string, fallback: string): string => {
  const flag = modulesMapper[key];
  return typeof flag === "string" ? flag : fallback;
};

const MODULE_FLAG = {
  SPACES: moduleFlag("SPACES", "SPACES"),
  TRANSMITTALS: moduleFlag("TRANSMITTALS", "TRANSMITTALS"),
  ISSUES: moduleFlag("ISSUES", "ISSUES"),
  TASKS: moduleFlag("TASKS", "TASKS"),
  REVIEWS: moduleFlag("REVIEWS", "REVIEWS"),
  ASSETS: moduleFlag("ASSETS", "ASSETS"),
  MAILBOX: moduleFlag("MAILBOX", "MAILBOX"),
  FILES: moduleFlag("FILES", "FILES"),
  ESTIMATES: moduleFlag("ESTIMATES", "ESTIMATES"),
  SCHEDULES: moduleFlag("SCHEDULES", "SCHEDULES"),
  CONTRACTS: moduleFlag("CONTRACTS", "CONTRACTS"),
  INSPECTION: moduleFlag("INSPECTION", "INSPECTION"),
  PROGRESS_UPDATES: moduleFlag("PROGRESS_UPDATES", "PROGRESS_UPDATES"),
  BILLS: moduleFlag("BILLS", "BILLS"),
  CHANGE_ORDER: moduleFlag("CHANGE_ORDER", "CHANGE_ORDER"),
  PHOTOS: moduleFlag("PHOTOS", "PHOTOS"),
  CLASH: moduleFlag("CLASH", "CLASH_TEST"),
};

/**
 * What a route builder produces. Web builds a URL string; the React Native host has no URLs and
 * builds the `[screenName, params]` pair it pushes onto its own navigation stack. Each host pins
 * `TRoute` to the arm it actually uses, so neither ever has to re-check the other's shape.
 */
export type StickySidebarRoute = string | [string, Record<string, unknown>];

/** Route builders the host supplies; each takes the projectId (Forms also takes a module name). */
export type StickySidebarPaths<TRoute extends StickySidebarRoute = string> = {
  Contract?: (projectId: string) => TRoute;
  Dashboard?: (projectId: string) => TRoute;
  Estimates?: (projectId: string) => TRoute;
  Files?: (projectId: string) => TRoute;
  Assets?: (projectId: string) => TRoute;
  GeneralClash?: (projectId: string) => TRoute;
  Issues?: (projectId: string) => TRoute;
  Photos?: (projectId: string) => TRoute;
  Mailbox?: (projectId: string) => TRoute;
  Reviews?: (projectId: string) => TRoute;
  Schedules?: (projectId: string) => TRoute;
  Tasks?: (projectId: string) => TRoute;
  Transmittals?: (projectId: string) => TRoute;
  Settings?: (projectId: string) => TRoute;
  WorkspacesList?: (projectId: string) => TRoute;
  Folders?: (projectId: string) => TRoute;
  Forms?: (projectId: string, moduleName: string) => TRoute;
};

/**
 * One sidebar entry. `allow` is deliberately `unknown`: web resolves modules by id and never reads it,
 * while the entries below build it from `path.X && layout.Y?.ENABLE`, whose truthy value is a route
 * builder or a layout-module record rather than a boolean. Only the mobile consumer filters on it.
 */
export type StickySidebarIcon<TRoute extends StickySidebarRoute = string> = {
  id: string;
  allow?: unknown;
  path?: TRoute | undefined;
  name: string;
  svg: string;
  toolTip?: string | undefined;
  count?: number | null | undefined;
  position?: string | undefined;
  showBetaTag?: boolean | undefined;
  /** Read only by the mobile consumer, which pushes onto its own navigation stack. */
  historyPush?: boolean | undefined;
};

type UseStickySidebarParams<TRoute extends StickySidebarRoute = string> = {
  layout: RootState["layout"];
  projectId: string;
  path: StickySidebarPaths<TRoute>;
  isFocused?: boolean;
  mobile?: boolean;
};

// Layouts is staging/QA-only for now. Omitted from the icon list entirely rather than flagged with
const SHOW_LAYOUTS_SIDEBAR_CELL = ["qa", "staging"].includes(process.env.REACT_APP_ENV || "");

function useStickySidebar<TRoute extends StickySidebarRoute = string>({
  layout,
  projectId,
  path,
  isFocused = true,
  mobile = false,
}: UseStickySidebarParams<TRoute>) {
  const { t } = useTranslation();

  const customModules = useSelector((state: RootState) => state.customModules);
  const portalData = useSelector((state: RootState) => state.portalData);

  const [counts, setCounts] = useState<Record<string, number> | null>(null);

  const isRustomjeePortal = portalData.frontendUrl?.includes("rustomjee") || false;
  const isDpmsPortal = portalData.frontendUrl?.includes("dpmsportal") || false;

  const dispatch = useDispatch();

  /**
   * `counts` is owned entirely by this request: `null` means "not loaded yet", which is why the reset
   * belongs to the fetch rather than to a prop change. Only the mobile host ever fetches (web passes no
   * `mobile`), so on web `counts` stays null for the lifetime of the hook and every `getCount` reads null.
   */
  const fetchModulesCount = useCallback(async () => {
    setCounts(null);
    const res = await axiosWrapper.post<{ status: boolean; result: Record<string, number> }>(
      `${getBackendUrl()}/projects/getModulesCount`,
      {
        projectId,
      },
    );
    if (res.data.status) {
      setCounts(res.data.result);
    }
  }, [projectId]);

  /**
   * ⚠️ BUG: nothing cancels an in-flight `getModulesCount`. `isFocused` is mobile's `useIsFocused`, so a
   * quick navigate-away-and-back re-runs this while the first request is still open; whichever response
   * lands last wins, and the earlier project's/attempt's counts can overwrite the newer ones. The reset
   * inside `fetchModulesCount` also means every refocus blanks the sidebar counts before they repaint.
   */
  useEffect(() => {
    // eslint-disable-next-line react-effect/no-event-handler -- unfixable here; see the adjacent note
    if (isFocused && mobile) {
      // Fire-and-forget: an effect body cannot be async; counts render null until it answers.
      void fetchModulesCount();
    }
  }, [isFocused, customModules, fetchModulesCount, mobile]);

  useEffect(() => {
    // Fire-and-forget thunk: the sidebar reads customModules off the store.
    void dispatch(getPortalCustomModules());
  }, []);

  function getCount(key: string) {
    return counts == null ? null : (counts[key] ?? 0);
  }

  /**
   * Deps deliberately exclude `path` and `t`: the web host builds `path` as a fresh object literal on
   * every render, so listing it here would rebuild this list — and re-dispatch the icon/name mapper —
   * on every render. The builders are pure functions of `projectId`, which IS listed.
   */
  const projectSideBarIcons = useMemo(() => {
    const icons: StickySidebarIcon<TRoute>[] = [
      {
        id: "Home",
        allow: !!path.Dashboard,
        path: path.Dashboard && path.Dashboard(projectId),
        name: t("Home"),
        svg: "HomeIcon",
        toolTip: isDpmsPortal ? `${modulesNameSynonymMapper.project} Dashboard` : t("Home"),
        position: "upper",
      },
      {
        // ONE entry branched on platform, not two entries sharing this id: the web sidebar resolves
        // modules by id (first match wins) and never reads `allow` — only the mobile consumer filters
        // on it — so a duplicate mobile-first entry would silently replace Folders on web.
        id: defaultModuleNamesMapper[MODULE_FLAG.SPACES]!,
        allow: (mobile ? path.WorkspacesList : path.Folders) && layout.SPACES?.ENABLE,
        path: mobile ? path.WorkspacesList && path.WorkspacesList(projectId) : path.Folders && path.Folders(projectId),
        count: getCount("Spaces"),
        // TODO: add additional properitey type because in dpmsportal, it is not highlighting
        name: mobile ? (isDpmsPortal ? `${modulesNameSynonymMapper.spaces}` : t("Spaces")) : t("Folders"),
        svg: mobile ? "SpacesIcon" : "FolderModule",
        toolTip: mobile ? (isDpmsPortal ? `${modulesNameSynonymMapper.spaces}` : t("Spaces")) : "Folders",
        position: "mid",
      },
      {
        id: defaultModuleNamesMapper[MODULE_FLAG.TRANSMITTALS]!,
        allow: path.Transmittals && layout.TRANSMITTAL?.ENABLE,
        path: path.Transmittals && path.Transmittals(projectId),
        count: getCount("Transmittals"),
        name: t("Transmittals"),
        svg: "TransmittalIcon",
        toolTip: t("Transmittals"),
        position: "mid",
      },
      {
        id: defaultModuleNamesMapper[MODULE_FLAG.ISSUES]!,
        allow: path.Issues && layout.ISSUES?.ENABLE,
        path: path.Issues && path.Issues(projectId),
        count: getCount("Issues"),
        name: t("Issues"),
        svg: "IssuesIcon",
        toolTip: t("Issues"),
        position: "mid",
      },
      {
        id: defaultModuleNamesMapper[MODULE_FLAG.TASKS]!,
        allow: path.Tasks && layout.TASKS?.ENABLE,
        path: path.Tasks && path.Tasks(projectId),
        count: getCount("Tasks"),
        name: t("Tasks"),
        svg: "TasksIcon",
        toolTip: t("Tasks"),
        position: "mid",
      },
      {
        id: defaultModuleNamesMapper[MODULE_FLAG.REVIEWS]!,
        allow: path.Reviews && layout.REVIEWFLOW?.ENABLE,
        path: path.Reviews && path.Reviews(projectId),
        count: getCount("Reviews"),
        name: t("Reviews"),
        svg: "Review",
        toolTip: t("Reviews"),
        position: "mid",
      },
      {
        id: defaultModuleNamesMapper[MODULE_FLAG.ASSETS]!,
        allow: path.Assets && layout.ASSETS?.ENABLE,
        path: path.Assets && path.Assets(projectId),
        count: getCount("Assets"),
        name: t("Assets"),
        svg: "Assets",
        toolTip: t("Assets"),
        position: "mid",
      },
      {
        id: defaultModuleNamesMapper[MODULE_FLAG.MAILBOX]!,
        allow: path.Mailbox && layout.MAILBOX?.ENABLE,
        path: path.Mailbox && path.Mailbox(projectId),
        name: t("Mailbox"),
        svg: "Mailbox",
        toolTip: t("Mailbox"),
        position: "mid",
      },
      {
        id: defaultModuleNamesMapper[MODULE_FLAG.FILES]!,
        allow: path.Files && layout.FILES?.ENABLE,
        path: path.Files && path.Files(projectId),
        count: getCount("Files"),
        name: t("Files"),
        svg: "FilesIcon",
        toolTip: t("Files"),
        position: "mid",
      },
      {
        id: defaultModuleNamesMapper[MODULE_FLAG.ESTIMATES]!,
        allow: path.Estimates && layout.ESTIMATES?.ENABLE,
        path: path.Estimates && path.Estimates(projectId),
        count: getCount("Estimates"),
        name: t("Estimates"),
        svg: "EstimateIcon",
        toolTip: t("Estimates"),
        position: "mid",
      },
      {
        id: defaultModuleNamesMapper[MODULE_FLAG.SCHEDULES]!,
        allow: path.Schedules && layout.SCHEDULES?.ENABLE,
        path: path.Schedules && path.Schedules(projectId),
        count: getCount("Schedules"),
        name: t("Schedules"),
        svg: "ScheduleIcon",
        toolTip: t("Schedules"),
        position: "mid",
      },
      {
        id: defaultModuleNamesMapper[MODULE_FLAG.CONTRACTS]!,
        allow: path.Contract && layout.CONTRACT?.ENABLE,
        count: getCount("Contracts"),
        historyPush: false,
        path: path.Contract && path.Contract(projectId),
        name: t("Contracts"),
        svg: "Contracts",
        toolTip: t("Contracts"),
        position: "mid",
      },
      {
        id: defaultModuleNamesMapper[MODULE_FLAG.INSPECTION]!,
        allow: path.Forms && layout.INSPECTION?.ENABLE,
        count: getCount(formCategories.inspection),
        path: path.Forms && path.Forms(projectId, formCategories.inspection),
        name: isRustomjeePortal ? "Work Done" : t("Inspections"),
        svg: "inspectionForm",
        toolTip: `${isRustomjeePortal ? "Work Done Forms" : t("Inspection Forms")}`,
        position: "mid",
      },
      {
        id: defaultModuleNamesMapper[MODULE_FLAG.PROGRESS_UPDATES]!,
        allow: path.Forms && layout.PROGRESS_UPDATES?.ENABLE,
        count: getCount(formCategories.progressUpdates),
        path: path.Forms && path.Forms(projectId, formCategories.progressUpdates),
        name: t("Progress Updates"),
        svg: "ProgressUpdates",
        toolTip: t("Progress Updates"),
        position: "mid",
      },
      {
        id: defaultModuleNamesMapper[MODULE_FLAG.BILLS]!,
        allow: path.Forms && layout.BILLS?.ENABLE,
        count: getCount(formCategories.bills),
        path: path.Forms && path.Forms(projectId, formCategories.bills),
        name: t("Bills"),
        svg: "bills",
        toolTip: t("Bills"),
        position: "mid",
      },
      {
        id: defaultModuleNamesMapper[MODULE_FLAG.CHANGE_ORDER]!,
        allow: path.Forms && layout.CHANGE_ORDER?.ENABLE,
        count: getCount(formCategories.changeOrder),
        path: path.Forms && path.Forms(projectId, formCategories.changeOrder),
        name: t("Change Orders"),
        svg: "changeOrder",
        toolTip: t("Change Orders"),
        position: "mid",
      },
      // {
      //   id: defaultModuleNamesMapper[MODULE_FLAG.PHOTOS]!,
      //   // Always visible for now; gate with `layout.PHOTOS?.ENABLE` once the backend layout flag exists.
      //   allow: !!path.Photos,
      //   path: path.Photos && path.Photos(projectId),
      //   name: t("Photos"),
      //   svg: "image",
      //   toolTip: t("Photos"),
      //   position: "mid",
      // },
      {
        id: defaultModuleNamesMapper[MODULE_FLAG.CLASH]!,
        showBetaTag: true,
        allow: path.GeneralClash && layout.CLASH_TEST?.ENABLE,
        path: path.GeneralClash && path.GeneralClash(projectId),
        name: t("Clash Tests"),
        svg: "ClashIcon",
        toolTip: t("Clash Tests"),
        position: "mid",
      },
      ...(SHOW_LAYOUTS_SIDEBAR_CELL
        ? [
            {
              id: LAYOUTS_SIDEBAR_CELL_ID,
              allow: true,
              name: t("Layouts"),
              svg: "MoreOptions",
              toolTip: t("Layouts"),
              position: "bottom",
            },
          ]
        : []),
      {
        id: "Settings",
        allow: true,
        path: path.Settings && path.Settings(projectId),
        name: t("Settings"),
        svg: "SettingsIcon",
        toolTip: isDpmsPortal ? `${modulesNameSynonymMapper.project} Settings` : t("Project Settings"),
        position: "bottom",
      },
    ];

    customModules.map((module: CustomModuleItem) => {
      icons.push({
        id: module.name!,
        allow: !!path.Forms,
        path: path.Forms && path.Forms(projectId, module.resourceType!),
        count: getCount(module.resourceType!),
        name: module.name!,
        svg: module.icon!,
        toolTip: module.name,
        position: "mid",
      });
    });
    return icons;
  }, [layout, projectId, customModules, counts, portalData]);

  useEffect(() => {
    dispatch(
      setProjectSideBarIconsAndNameMapper(
        projectSideBarIcons.map(({ id, name, svg, toolTip }) => {
          return { id, name, svg, toolTip };
        }),
      ),
    );
  }, [projectSideBarIcons, dispatch]);

  const sideBarMidIcons = useMemo(
    () => projectSideBarIcons.filter((icon) => icon.position === "mid"),
    [projectSideBarIcons],
  );
  const sideBarUpperFixedIcons = useMemo(
    () => projectSideBarIcons.filter((icon) => icon.position === "upper"),
    [projectSideBarIcons],
  );
  const sideBarLowerFixedIcons = useMemo(
    () => projectSideBarIcons.filter((icon) => icon.position === "bottom"),
    [projectSideBarIcons],
  );

  return { sideBarMidIcons, sideBarUpperFixedIcons, sideBarLowerFixedIcons };
}

export default useStickySidebar;
